import { cn } from 'designSystem/lib/utils'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from 'lucide-react'
import * as React from 'react'

import { Button } from './Button'

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      role='navigation'
      aria-label='pagination'
      data-slot='pagination'
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  )
}

function PaginationContent({
  className,
  ...props
}: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot='pagination-content'
      className={cn('flex items-center gap-0.5', className)}
      {...props}
    />
  )
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
  return <li data-slot='pagination-item' {...props} />
}

function PaginationFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='pagination-footer'
      className={cn(
        'flex min-h-12 w-full shrink-0 items-center justify-between gap-2.5 overflow-x-auto border-t border-border px-4 py-2',
        className
      )}
      {...props}
    />
  )
}

function PaginationFooterStatus({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot='pagination-footer-status'
      className={cn(
        'shrink-0 whitespace-nowrap text-sm leading-5 font-medium text-muted-foreground',
        className
      )}
      {...props}
    />
  )
}

function PaginationFooterActions({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='pagination-footer-actions'
      className={cn(
        "flex shrink-0 items-center justify-end [&_[data-slot=pagination]]:w-auto [&_[data-slot=pagination]]:justify-end [&_[data-slot=pagination-content]]:gap-1 [&_[data-slot=pagination-ellipsis]]:size-8 [&_[data-slot=pagination-ellipsis]_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  )
}

type PaginationLinkProps = React.ComponentProps<'a'> & {
  isActive?: boolean
} & Pick<React.ComponentProps<typeof Button>, 'size'>

function PaginationLink({
  className,
  isActive,
  size = 'icon',
  ...props
}: PaginationLinkProps) {
  return (
    <Button
      asChild
      variant={isActive ? 'outline' : 'ghost'}
      size={size}
      className={cn(
        'aria-disabled:pointer-events-none aria-disabled:opacity-50',
        className
      )}>
      <a
        aria-current={isActive ? 'page' : undefined}
        data-slot='pagination-link'
        data-active={isActive}
        {...props}
      />
    </Button>
  )
}

function PaginationPrevious({
  className,
  text = 'Previous',
  ...props
}: React.ComponentProps<typeof PaginationLink> & { text?: string }) {
  return (
    <PaginationLink
      aria-label='Go to previous page'
      size='default'
      className={cn('pl-2!', className)}
      {...props}>
      <ChevronLeftIcon
        aria-hidden='true'
        data-icon='inline-start'
        className='cn-rtl-flip'
      />
      <span className='hidden sm:block'>{text}</span>
    </PaginationLink>
  )
}

function PaginationNext({
  className,
  text = 'Next',
  ...props
}: React.ComponentProps<typeof PaginationLink> & { text?: string }) {
  return (
    <PaginationLink
      aria-label='Go to next page'
      size='default'
      className={cn('pr-2!', className)}
      {...props}>
      <span className='hidden sm:block'>{text}</span>
      <ChevronRightIcon
        aria-hidden='true'
        data-icon='inline-end'
        className='cn-rtl-flip'
      />
    </PaginationLink>
  )
}

function PaginationEllipsis({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      aria-hidden='true'
      data-slot='pagination-ellipsis'
      className={cn(
        "flex size-7 items-center justify-center [&_svg:not([class*='size-'])]:size-3.5",
        className
      )}
      {...props}>
      <MoreHorizontalIcon aria-hidden='true' />
      <span className='sr-only'>More pages</span>
    </span>
  )
}

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationFooter,
  PaginationFooterActions,
  PaginationFooterStatus,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}
